import React, { useEffect, useState } from "react";
import BoardTable from "../../common/board/BoardTable";
import BoardPagination from "../../common/board/BoardPagination";
import BoardToolbar from "../../common/board/BoardToolbar";
import SearchBar from "../../common/board/SearchFilterBar";
import { getWorkOrderList } from "../../../api/const";
import { useParams } from "react-router-dom";

export default function WorkOrderSelect({ isOpen, onClose, onSelect }) {
  const { id: projectId } = useParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 검색 및 페이징 상태
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const getStatusBadge = (s) => {
    switch (s) {
      case "진행중": return "bg-yellow-100 text-yellow-800";
      case "완료보고서 등록됨": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // 테이블 컬럼 구성
  const columns = [
    { key: "title", header: "제목", width: 350, render: (row) => <div className="truncate">{row.title}</div> },
    { key: "created_at", header: "작성일", width: 160, render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString("ko-KR") : "-" },
    {
      key: "status", header: "상태", width: 120,
      render: (row) => {
        const label = row.completion?.length > 0 ? "완료보고서 등록됨" : "진행중";
        return <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadge(label)}`}>{label}</span>;
      }
    },
    { key: "created_by", header: "작성자", width: 120, render: (row) => row.created_by?.name || "-" },
  ];

  const fetchData = async () => {
    if (!projectId || !isOpen) return;
    setLoading(true);
    setError("");

    try {
      const params = {
        project: projectId,
        [searchField]: q,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        page_size: pageSize,
      };

      const res = await getWorkOrderList(projectId, params);
      setRows(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch (e) {
      console.error(e);
      setError("작업지시서 데이터를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, page, pageSize, searchTrigger]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* 헤더 */}
        <div className="flex justify-between items-center border-b pb-3 mb-4">
          <h2 className="text-xl font-bold">작업지시서 선택</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">
            ×
          </button>
        </div>
        <div className="p-4 border-b bg-gray-50">
          <BoardToolbar
            right={
            <>
              <SearchBar
                q={q}
                onChangeQ={setQ}
                searchField={searchField}
                onChangeSearchField={setSearchField}
                dateFilter={dateFilter}
                onChangeDateFilter={setDateFilter}
                startDate={startDate}
                endDate={endDate}
                onDateChange={({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                onSearch={handleSearch}
                pageSize={pageSize}
                onChangePageSize={setPageSize}
              />
          </>
          }
          />
          </div>

        {/* 테이블 */}
        <div className="flex-1 overflow-auto">
          <BoardTable
            columns={columns}
            rows={rows}
            loading={loading}
            error={error}
            keyField="id"
            emptyText="작업지시서가 없습니다."
            onRowClick={(row) => {
              onSelect({
                dns: row.dns,
                title: row.title,
                project_name: row.project_name,
                start_date: row.start_date,
                status: row.status,
              });
              onClose();
            }}
          />
        </div>

        {/* 페이지네이션 */}
        <div className="pt-4">
          <BoardPagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
          />
        </div>

      </div>
    </div>
  );
}
